import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Navbar.css';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [housingMenuOpen, setHousingMenuOpen] = useState(false);
  const [housingForms, setHousingForms] = useState([]);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const housingMenuRef = useRef(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setHousingMenuOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavClick = (e, path) => {
    // Prevent navigation if already on this page
    if (location.pathname === path) {
      e.preventDefault();
    }
    closeMobileMenu();
  };

  const isHousingDynamicFormPath = location.pathname.startsWith('/demander-logement/form/');
  const isHousingMenuPath = ['/demander-logement', '/signup', '/demande-transfert', '/statut-demandes'].includes(location.pathname)
    || isHousingDynamicFormPath;

  const toggleHousingMenu = () => {
    setHousingMenuOpen((prev) => !prev);
  };

  const closeHousingMenu = () => {
    setHousingMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
  };

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    closeHousingMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!housingMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (housingMenuRef.current && !housingMenuRef.current.contains(event.target)) {
        closeHousingMenu();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [housingMenuOpen]);

  useEffect(() => {
    if (!housingMenuOpen) return undefined;

    let cancelled = false;

    const loadHousingForms = async () => {
      try {
        const response = await api.forms.listPublic();
        if (cancelled) return;

        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        const additionalForms = rows.filter((form) => {
          const slug = String(form?.slug || '').trim().toLowerCase();
          return Boolean(slug) && slug !== 'registration-form';
        });

        setHousingForms(additionalForms);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load housing form links:', error);
        setHousingForms([]);
      }
    };

    loadHousingForms();

    return () => {
      cancelled = true;
    };
  }, [housingMenuOpen]);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
          <img src="/logo.png" alt="ADEEF" className="logo-image" />
        </Link>

        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}></span>
        </button>

        <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <Link 
              to="/" 
              className={`nav-link nav-tab ${isActive('/') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/')}
            >
              <span className="nav-tab-text">Accueil</span>
            </Link>
          </li>
          <li ref={housingMenuRef} className={`nav-item nav-item-dropdown ${housingMenuOpen ? 'open' : ''}`}>
            <button
              type="button"
              className={`nav-link nav-tab nav-dropdown-toggle ${isHousingMenuPath ? 'active' : ''}`}
              onClick={toggleHousingMenu}
              aria-expanded={housingMenuOpen}
              aria-haspopup="true"
            >
              <span className="nav-tab-text">Demander un logement</span>
              <span className={`nav-dropdown-caret ${housingMenuOpen ? 'open' : ''}`}>▾</span>
            </button>

            <div className={`nav-dropdown-menu ${housingMenuOpen ? 'open' : ''}`}>
              <Link
                to="/demander-logement"
                className={`nav-dropdown-link ${isActive('/demander-logement') || isActive('/signup') ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, '/demander-logement')}
              >
                Demander un logement
              </Link>

              {housingForms.map((form) => {
                const formPath = `/demander-logement/form/${encodeURIComponent(form.slug)}`;

                return (
                  <Link
                    key={form.id || form.slug}
                    to={formPath}
                    className={`nav-dropdown-link ${isActive(formPath) ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, formPath)}
                  >
                    {form.form_name || form.slug}
                  </Link>
                );
              })}

              <Link
                to="/statut-demandes"
                className={`nav-dropdown-link ${(isActive('/demande-transfert') || isActive('/statut-demandes')) ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, '/statut-demandes')}
              >
                Statut des demandes
              </Link>
            </div>
          </li>
          <li className="nav-item">
            <Link 
              to="/events" 
              className={`nav-link nav-tab ${isActive('/events') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/events')}
            >
              <span className="nav-tab-text">Events</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link 
              to="/guide-adeef" 
              className={`nav-link nav-tab ${isActive('/guide-adeef') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/guide-adeef')}
            >
              <span className="nav-tab-text">Guide Adeef</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link 
              to="/temoignages" 
              className={`nav-link nav-tab ${isActive('/temoignages') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/temoignages')}
            >
              <span className="nav-tab-text">Système de témoignage</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link 
              to="/faq" 
              className={`nav-link nav-tab ${isActive('/faq') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/faq')}
            >
              <span className="nav-tab-text">FAQ</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link 
              to="/contact" 
              className={`nav-link nav-tab ${isActive('/contact') ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, '/contact')}
            >
              <span className="nav-tab-text">Nous Contacter</span>
            </Link>
          </li>
        </ul>

        <div className="nav-auth">
          {user ? (
            <div className="user-menu">
              <Link 
                to={isAdmin ? '/admin' : '/dashboard'}
                className="tenant-btn"
                onClick={closeMobileMenu}
              >
                MON ESPACE
              </Link>
              <button onClick={handleLogout} className="logout-btn">
                Déconnexion
              </button>
            </div>
          ) : (
            <Link to="/login" className="bubbles" onClick={closeMobileMenu}>
              <span className="text">TON ESPACE LOCATAIRE</span>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
